import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "@/lib/api";

const LS_DISMISSED = "fs_popups_dismissed"; // JSON array of pop-up ids
const SHOW_DELAY_MS = 6000;
const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// Blacklist — pop-ups must NOT show during the Ronin flow, checkout/booking/payments,
// auth gates, admin, or the standalone round timer (a workout surface).
const BLOCKED_PREFIXES = ["/admin", "/portal", "/book", "/payment", "/login", "/register", "/ronin-box", "/round-timer", "/forgot-password", "/reset-password", "/verify-email"];
function isBlocked(pathname) {
  if (!pathname) return true;
  return BLOCKED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

function getDismissed() {
  try { return JSON.parse(localStorage.getItem(LS_DISMISSED) || "[]"); } catch { return []; }
}
function addDismissed(id) {
  try {
    const arr = getDismissed();
    if (!arr.includes(id)) { arr.push(id); localStorage.setItem(LS_DISMISSED, JSON.stringify(arr)); }
  } catch { /* ignore */ }
}

export function PopupManager() {
  const location = useLocation();
  const navigate = useNavigate();
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  const blocked = isBlocked(location.pathname);

  const fetchActive = useCallback(async () => {
    try {
      const { data } = await api.get("/popups/active?platform=web");
      const dismissed = getDismissed();
      const next = (data || []).find((p) => !dismissed.includes(p.id));
      return next || null;
    } catch { return null; }
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    setVisible(false);
    if (blocked) return;
    let cancelled = false;
    (async () => {
      const next = await fetchActive();
      if (cancelled || !next) return;
      setPopup(next);
      timerRef.current = setTimeout(() => {
        if (cancelled) return;
        setVisible(true);
        api.post(`/popups/${next.id}/event`, { type: "impression" }).catch(() => {});
      }, SHOW_DELAY_MS);
    })();
    return () => { cancelled = true; clearTimeout(timerRef.current); };
  }, [blocked, location.pathname, fetchActive]);

  const close = () => {
    if (popup) { addDismissed(popup.id); api.post(`/popups/${popup.id}/event`, { type: "dismiss" }).catch(() => {}); }
    setVisible(false);
  };

  const onCta = () => {
    if (!popup) return;
    addDismissed(popup.id);
    api.post(`/popups/${popup.id}/event`, { type: "click" }).catch(() => {});
    setVisible(false);
    const url = popup.cta_url;
    if (!url) return;
    if (/^https?:\/\//i.test(url)) window.open(url, "_blank", "noopener,noreferrer");
    else navigate(url);
  };

  if (!visible || !popup || blocked) return null;

  const imgSrc = popup.image_url ? (popup.image_url.startsWith("http") ? popup.image_url : `${apiBase}${popup.image_url}`) : null;

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200" onClick={close} data-testid="popup-overlay">
      <div className="relative w-full sm:max-w-md bg-[#0A0A0A] border border-[#27272A] border-t-4 border-t-[#FF331F] rounded-t-md sm:rounded-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="popup-card">
        <button type="button" onClick={close} className="absolute top-3 right-3 z-10 p-1.5 bg-black/60 hover:bg-[#FF331F] text-white rounded-sm transition-colors" data-testid="popup-close" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
        {imgSrc && (
          <div className="w-full h-40 bg-[#050505] overflow-hidden">
            <img src={imgSrc} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-6 sm:p-7">
          <h2 className="font-display text-2xl text-white uppercase tracking-tight leading-tight mb-2" data-testid="popup-title">{popup.title}</h2>
          <p className="text-sm text-zinc-400 leading-relaxed mb-5" data-testid="popup-message">{popup.message}</p>
          {popup.cta_label && popup.cta_url ? (
            <button onClick={onCta} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs rounded-sm h-12 transition-colors" data-testid="popup-cta">
              {popup.cta_label}
            </button>
          ) : (
            <button onClick={close} className="w-full border border-[#27272A] hover:border-[#FF331F] text-zinc-300 hover:text-white font-bold uppercase tracking-[0.2em] text-xs rounded-sm h-12 transition-colors" data-testid="popup-dismiss">
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
